"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, Search, Sparkles, TrendingUp } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
const trustPoints = [{
  id: 1,
  text: "Tailored SEO strategy"
}, {
  id: 2,
  text: "Hands-on strategy & execution"
}, {
  id: 3,
  text: "Clear, transparent reporting"
}];
export default function ConsultantHero() {
  const shouldReduceMotion = useReducedMotion();

  // Subtle breadcrumbs check if available
  const [breadcrumbs, setBreadcrumbs] = React.useState<Array<{
    name: string;
    url: string;
  }>>([]);
  React.useEffect(() => {
    getWvcClient()?.pluginRequest("wvc-seo/v1/breadcrumbs", {
      params: {
        url: "/HomePage"
      },
      cache: true
    }).then((data: {
      items: Array<{
        name: string;
        url: string;
      }>;
    }) => {
      if (data?.items && data.items.length > 1) {
        setBreadcrumbs(data.items);
      }
    }).catch(() => {
      // Silently degrade as per guidelines
    });
  }, []);
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultanthero" data-nav="light" className="relative overflow-hidden bg-background text-foreground pt-28 pb-20 md:pt-36 md:pb-28 border-b border-border">
      {/* Subtle background ambient glow (cobalt & blush accents) */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -left-20 h-96 w-96 rounded-full bg-secondary/60 blur-3xl opacity-70" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/3 -right-24 h-96 w-96 rounded-full bg-accent/15 blur-3xl opacity-60" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {breadcrumbs.length > 1 && <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {breadcrumbs.map((crumb, idx) => <li key={idx} data-index={idx} className="flex items-center gap-2">
                  {idx < breadcrumbs.length - 1 && crumb.url ? <Link to={crumb.url} className="hover:text-foreground transition-colors">
                      {crumb.name}
                    </Link> : <span aria-current="page" className="text-foreground font-medium">
                      {crumb.name}
                    </span>}
                  {idx < breadcrumbs.length - 1 && <span aria-hidden="true">/</span>}
                </li>)}
            </ol>
          </nav>}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column: Copy & CTAs */}
          <div className="lg:col-span-7 flex flex-col items-start">
            <motion.div initial="hidden" animate="visible" variants={fadeUpVariant} className="inline-flex items-center gap-2 mb-4">
              <Badge variant="secondary" className="px-3.5 py-1 text-xs font-medium rounded-full bg-secondary text-secondary-foreground border border-border flex items-center gap-1.5">
                <Sparkles className="size-3.5 text-accent-foreground" />
                <span>Independent Freelance SEO Consultant &bull; Swindon & Nationwide UK</span>
              </Badge>
            </motion.div>

            <motion.h1 initial="hidden" animate="visible" variants={fadeUpVariant} className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-[1.15] mb-6">
              SEO Consultant UK for Sustainable Search Growth
            </motion.h1>

            <motion.p initial="hidden" animate="visible" variants={fadeUpVariant} className="text-base sm:text-lg text-muted-foreground max-w-2xl mb-8 leading-relaxed">
              I help UK and international businesses secure genuine search visibility and qualified organic leads. My consultancy delivers senior strategic guidance and technical execution focused on competitive UK search landscapes, commercial intent, and sustainable organic growth.
            </motion.p>

            {/* Direct Consultant Promise */}
            <motion.div initial="hidden" animate="visible" variants={fadeUpVariant} className="w-full bg-muted/50 border border-border rounded-lg p-4 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-2.5 font-medium text-foreground">
                <ShieldCheck className="size-5 text-primary shrink-0" />
                <span>Senior strategic oversight & execution</span>
              </div>
              <span className="text-xs text-muted-foreground sm:border-l sm:border-border sm:pl-3">
                Practical implementation & honest metrics
              </span>
            </motion.div>

            {/* Actions */}
            <motion.div initial="hidden" animate="visible" variants={fadeUpVariant} className="flex flex-wrap items-center gap-4 w-full sm:w-auto mb-8">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-medium shadow-sm transition-[background-color,box-shadow]">
                <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-border text-foreground hover:bg-accent/20 rounded-md font-medium transition-[background-color,border-color]">
                <Link to="/CaseStudiesPage" className="inline-flex items-center justify-center gap-2">
                  <TrendingUp className="size-4 text-primary" />
                  <span>Explore My Case Studies</span>
                </Link>
              </Button>
            </motion.div>

            {/* Genuine Value Props List */}
            <motion.div initial="hidden" animate="visible" variants={fadeUpVariant} className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-6 border-t border-border w-full">
              {trustPoints.map((item, idx) => <div key={item.id} data-index={idx} className="flex items-center gap-2 text-xs font-medium text-foreground/90">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{item.text}</span>
                </div>)}
            </motion.div>
          </div>

          {/* Right Column: Consultant Profile Feature */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <motion.div initial="hidden" animate="visible" variants={fadeUpVariant} className="relative w-full max-w-md">
              {/* Decorative framing element */}
              <div aria-hidden="true" className="absolute -inset-2 rounded-2xl bg-gradient-to-tr from-secondary/80 via-transparent to-accent/20 -z-10 blur-sm" />

              <div className="bg-card text-card-foreground border border-border rounded-xl shadow-md overflow-hidden p-6">
                <div className="relative aspect-4/5 w-full rounded-lg overflow-hidden bg-muted mb-5 border border-border/60">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Independent Freelance SEO Consultant" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                  <div className="absolute bottom-3 left-3 right-3 bg-background/90 backdrop-blur-xs border border-border/80 rounded-md py-2 px-3 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-foreground">Yassine Essaadi</p>
                      <p className="text-[11px] text-muted-foreground">Founder & Principal Consultant</p>
                    </div>
                    <Badge variant="outline" className="text-[11px] border-border bg-card">
                      YE Consultancy
                    </Badge>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs pb-3 border-b border-border">
                    <span className="text-muted-foreground">Location</span>
                    <span className="font-medium text-foreground">Swindon, Wiltshire & UK-Wide</span>
                  </div>
                  <div className="flex items-center justify-between text-xs pb-3 border-b border-border">
                    <span className="text-muted-foreground">Specialisms</span>
                    <span className="font-medium text-foreground">National, Technical & AI Search</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Consultancy Model</span>
                    <span className="font-medium text-foreground">Independent Specialist</span>
                  </div>
                </div>

                {/* Micro CTA badge */}
                <div className="mt-5 pt-4 border-t border-border flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Search className="size-4 text-primary" />
                    <span className="text-xs text-muted-foreground">Search Strategy & Audits</span>
                  </div>
                  <Link to="/AboutMePage" className="text-xs font-medium text-primary hover:underline">
                    Read my bio
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}